import { PlayActivity } from "../components/PlayActivity.js";
import { LiveControlsPage } from "./LiveControlsPage.js";
import { Link } from "react-router-dom";
/**
 * Spec §14.1 overview cards. Every value comes from a live endpoint; anything the backend
 * does not report to parents is rendered as "not reported" — never invented.
 */
import {
  useApps,
  useCapabilities,
  useConsents,
  useDevice,
  useHealth,
  usePolicies,
  useReports,
  useRequests,
  useUsage,
} from "../api/hooks.js";
import { CoverageNote } from "../components/StateBadge.js";
import { Empty, PageState } from "../components/PageState.js";

export function DashboardPage({ deviceId }: { deviceId: string }) {
  const device = useDevice(deviceId);
  const caps = useCapabilities(deviceId);
  const policies = usePolicies(deviceId);
  const usage = useUsage(deviceId);
  const apps = useApps(deviceId);
  const requests = useRequests(deviceId);
  const consents = useConsents(deviceId);
  const reports = useReports(deviceId);
  const health = useHealth();

  if (!deviceId)
    return <Empty label="No device selected — enroll a Quest headset first." />;

  const dailyCap = (policies.data ?? []).find(
    (p) => p.type === "DEVICE_PLAYTIME_DAILY" && p.enabled,
  );
  const usedSeconds = usage.data?.observed_seconds ?? 0;

  const newest = (reports.data ?? [])[0];

  const audioState = (consents.data ?? []).some((c) => !c.revoked_at)
    ? "CONSENT ACTIVE"
    : "NO CONSENT ON FILE";

  return (
    <PageState query={{ isPending: device.isPending, error: device.error }}>
      <section className="overview-hero">
        <div>
          <p className="eyebrow">ROOM TO PLAY. TIME TO PAUSE.</p>
          <h2>{device.data?.nickname ?? "Your Quest"}</h2>
          <p>Your household’s boundaries, wherever the adventure goes.</p>
          <div className="row">
            <Link className="button" to="/live">
              Open live controls ↗
            </Link>
            <Link className="button secondary" to="/schedules">
              Plan playtime
            </Link>
          </div>
        </div>
        <div className="orbit" aria-hidden="true">
          <div className="headset">
            <span />
            <span />
          </div>
          <span className="orbit-label">PLAY WITH BALANCE</span>
        </div>
      </section>
      <PlayActivity key={deviceId} deviceId={deviceId} />
      <div className="quick-controls">
        <LiveControlsPage key={deviceId} deviceId={deviceId} compact />
      </div>
      <div className="grid metrics">
        <Card
          label="Connection"
          value={
            device.data?.last_seen_at &&
            Date.now() - new Date(device.data.last_seen_at).getTime() < 120000
              ? "Connected"
              : "Not connected"
          }
          hint={`Last check-in ${formatTime(device.data?.last_seen_at)}`}
        />
        <Card
          label="Reported usage history"
          value={
            usage.data ? `${Math.round(usedSeconds / 60)} min` : "Not reported"
          }
          hint="Historical observations; today’s allowance counter is above"
        />
        <Card
          label="Base daily limit"
          value={
            policies.isPending || policies.error
              ? "Not reported"
              : dailyCap
                ? `${dailyCap.parameters.minutes} min`
                : "No daily cap"
          }
          hint="Scheduled daily limit, before extra time"
        />
        <Card
          label="Battery"
          value={
            device.data?.battery_percent == null
              ? "Not reported"
              : `${device.data.battery_percent}%`
          }
          hint={`Updated ${formatTime(device.data?.health_reported_at)}`}
        />
      </div>
      <div className="grid overview-links">
        <Link className="card shortcut" to="/library">
          <span className="shortcut-icon">▦</span>
          <div>
            <h3>Your game library</h3>
            <p>
              {apps.data
                ? `${apps.data.length} titles to explore and manage`
                : "Manage installed titles"}
            </p>
          </div>
          <span>↗</span>
        </Link>
        <Link className="card shortcut" to="/live">
          <span className="shortcut-icon">◷</span>
          <div>
            <h3>Requests for more time</h3>
            <p>
              {requests.data
                ? `${requests.data.length} waiting for your review`
                : "Review playtime requests"}
            </p>
          </div>
          <span>↗</span>
        </Link>
      </div>
      <details className="card">
        <summary>Device details & service health</summary>
        <div className="grid detail-grid">
          <Card
            label="Firmware / OS"
            value={device.data?.os_version ?? "Not reported"}
            hint="Device registration"
          />
          <Card
            label="Free storage"
            value={
              device.data?.storage_free_bytes != null
                ? formatBytes(device.data.storage_free_bytes)
                : "Not reported"
            }
            hint="Available device storage"
          />
          <Card
            label="Queued events"
            value={
              device.data?.unsent_event_count == null
                ? "Not reported"
                : String(device.data.unsent_event_count)
            }
            hint="Waiting to sync"
          />
          <Card
            label="Audio consent"
            value={audioState}
            hint="Consent ledger status"
          />
          <Card
            label="Service health"
            value={health.data?.status ?? "Unknown"}
            hint="Latest readiness check"
          />
          <Card
            label="Local restrictions"
            value={stateOf(caps, "local_restrictions")}
            hint="Latest reported capability"
          />
        </div>
      </details>
      {newest ? (
        <section className="card stack">
          <h3>Tonight&apos;s report</h3>
          <CoverageNote coverage={newest.coverage} />
          <p className="muted">
            {newest.report_date_local} — status {newest.status}
          </p>
        </section>
      ) : (
        <p className="notice">No nightly report yet.</p>
      )}
      <p className="notice warn">
        Coverage: playtime is only as accurate as device reporting; unknown
        intervals are counted separately ({usage.data?.unknown_intervals ?? 0}{" "}
        this period).
      </p>
    </PageState>
  );
}

function Card({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="card">
      <h3>{label}</h3>
      <div className="value">{value}</div>
      <div className="hint">{hint}</div>
    </div>
  );
}

function stateOf(caps: ReturnType<typeof useCapabilities>, name: string) {
  const row = (caps.data ?? []).find((c) => c.capability === name);
  return row ? row.state : "untested";
}

function formatTime(iso: string | null | undefined) {
  return iso ? new Date(iso).toLocaleString() : "never";
}

/** Bytes only render as GiB/MiB when the agent actually reported them. */
function formatBytes(bytes: number) {
  const gib = bytes / 1024 ** 3;
  return gib >= 1
    ? `${gib.toFixed(1)} GiB`
    : `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
}
